import React, { useEffect, useState, useRef } from 'react';
import { XIcon, AlertTriangleIcon, TruckIcon, MicIcon, StopCircleIcon } from 'lucide-react';
interface RequestMaterialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (requestData: any) => void;
  jobName: string;
  jobNumber: string;
  currentLocation: string;
  foremanName: string;
}
export const RequestMaterialsModal: React.FC<RequestMaterialsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  jobName,
  jobNumber,
  currentLocation,
  foremanName
}) => {
  const [material, setMaterial] = useState('Road Base');
  const [truckType, setTruckType] = useState('dump truck 12');
  const [requestType, setRequestType] = useState('trucks');
  const [trucksRequested, setTrucksRequested] = useState<number | undefined>(1);
  const [tonnageRequested, setTonnageRequested] = useState<number | undefined>();
  const [requestedDate, setRequestedDate] = useState('');
  const [requestTime, setRequestTime] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [reason, setReason] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const reasonInputRef = useRef<HTMLTextAreaElement>(null);
  const recognition = useRef<SpeechRecognition | null>(null);
  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setRequestedDate(tomorrow.toISOString().split('T')[0]);
    // Default time to 8:00 AM
    setRequestTime('08:00');
  }, []);
  // Initialize speech recognition
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.onresult = event => {
        const transcript = Array.from(event.results).map(result => result[0].transcript).join('');
        setReason(transcript);
        // Focus and move cursor to end of textarea
        if (reasonInputRef.current) {
          reasonInputRef.current.focus();
          reasonInputRef.current.selectionStart = reasonInputRef.current.value.length;
          reasonInputRef.current.selectionEnd = reasonInputRef.current.value.length;
        }
      };
      recognition.current.onerror = event => {
        console.error('Speech recognition error', event.error);
        setRecordingError('Error with speech recognition. Please try again.');
        setIsRecording(false);
      };
      recognition.current.onend = () => {
        setIsRecording(false);
      };
    }
    return () => {
      if (recognition.current) {
        recognition.current.onresult = null;
        recognition.current.onend = null;
        recognition.current.onerror = null;
        if (isRecording) {
          recognition.current.stop();
        }
      }
    };
  }, []);
  const toggleRecording = () => {
    if (!recognition.current) {
      setRecordingError('Speech recognition is not supported in your browser.');
      return;
    }
    setRecordingError('');
    if (isRecording) {
      recognition.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognition.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setRecordingError('Could not start recording. Please try again.');
      }
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    if (!material || !truckType || !requestedDate || !requestTime) {
      alert('Please fill in all required fields.');
      return;
    }
    if (requestType === 'trucks' && !trucksRequested) {
      alert('Please specify how many trucks you need.');
      return;
    }
    if (requestType === 'tonnage' && !tonnageRequested) {
      alert('Please specify how many tons you need.');
      return;
    }
    if (requestType === 'both' && (!trucksRequested || !tonnageRequested)) {
      alert('Please specify both trucks and tonnage for your request.');
      return;
    }
    // Create request data
    const requestData = {
      id: `req-${Date.now()}`,
      jobName,
      jobNumber,
      currentLocation,
      foremanName,
      material,
      truckType,
      requestType,
      trucksRequested,
      tonnageRequested,
      requestedDate,
      requestTime,
      urgency,
      reason,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    onSubmit(requestData);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-3525">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl" data-id="element-3526">
        <div className="p-4 border-b flex justify-between items-center" data-id="element-3527">
          <h3 className="font-bold text-lg" data-id="element-3528">Request Materials</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-3529">
            <XIcon size={20} data-id="element-3530" />
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-3531">
          <div className="p-4" data-id="element-3532">
            <div className="mb-4" data-id="element-3533">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3534">
                Job
              </label>
              <div className="p-3 bg-gray-100 rounded-md" data-id="element-3535">
                <div className="font-medium" data-id="element-3536">{jobName}</div>
                <div className="text-sm text-gray-500" data-id="element-3537">{jobNumber}</div>
              </div>
            </div>
            <div className="mb-4" data-id="element-3538">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3539">
                Material Type*
              </label>
              <select value={material} onChange={e => setMaterial(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" required data-id="element-3540">
                <option value="Road Base" data-id="element-3541">Road Base</option>
                <option value="Gravel" data-id="element-3542">Gravel</option>
                <option value="Sand" data-id="element-3543">Sand</option>
                <option value="Fill Dirt" data-id="element-3544">Fill Dirt</option>
                <option value="Topsoil" data-id="element-3545">Topsoil</option>
                <option value="Asphalt" data-id="element-3546">Asphalt</option>
                <option value="Concrete" data-id="element-3547">Concrete</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-3548">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3549">
                Truck Type*
              </label>
              <select value={truckType} onChange={e => setTruckType(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" required data-id="element-3550">
                <option value="dump truck 10" data-id="element-3551">Dump Truck (10 yard)</option>
                <option value="dump truck 12" data-id="element-3552">Dump Truck (12 yard)</option>
                <option value="dump truck 14+" data-id="element-3553">Dump Truck (14+ yard)</option>
                <option value="strong arm 12" data-id="element-3554">Strong Arm (12 yard)</option>
                <option value="strong arm 14" data-id="element-3555">Strong Arm (14 yard)</option>
                <option value="strong arm 16+" data-id="element-3556">Strong Arm (16+ yard)</option>
                <option value="side dump" data-id="element-3557">Side Dump</option>
                <option value="super side 30tn+" data-id="element-3558">Super Side (30+ ton)</option>
                <option value="jumbo side 35tn+" data-id="element-3559">Jumbo Side (35+ ton)</option>
                <option value="belly dump" data-id="element-3560">Belly Dump</option>
                <option value="double belly" data-id="element-3561">Double Belly</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-3562">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3563">
                Request Type*
              </label>
              <div className="flex gap-3" data-id="element-3564">
                <label className="flex items-center" data-id="element-3565">
                  <input type="radio" name="requestType" value="trucks" checked={requestType === 'trucks'} onChange={() => setRequestType('trucks')} className="mr-2" data-id="element-3566" />
                  <span data-id="element-3567">Number of Trucks</span>
                </label>
                <label className="flex items-center" data-id="element-3568">
                  <input type="radio" name="requestType" value="tonnage" checked={requestType === 'tonnage'} onChange={() => setRequestType('tonnage')} className="mr-2" data-id="element-3569" />
                  <span data-id="element-3570">Tonnage</span>
                </label>
                <label className="flex items-center" data-id="element-3571">
                  <input type="radio" name="requestType" value="both" checked={requestType === 'both'} onChange={() => setRequestType('both')} className="mr-2" data-id="element-3572" />
                  <span data-id="element-3573">Both</span>
                </label>
              </div>
            </div>
            {(requestType === 'trucks' || requestType === 'both') && <div className="mb-4" data-id="element-3574">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3575">
                  Number of Trucks Needed*
                </label>
                <div className="flex items-center" data-id="element-3576">
                  <input type="number" min="1" max="20" value={trucksRequested || ''} onChange={e => setTrucksRequested(parseInt(e.target.value) || undefined)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" required={requestType === 'trucks' || requestType === 'both'} data-id="element-3577" />
                  <TruckIcon size={18} className="text-gray-400 -ml-8" data-id="element-3578" />
                </div>
              </div>}
            {(requestType === 'tonnage' || requestType === 'both') && <div className="mb-4" data-id="element-3579">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3580">
                  Tonnage Needed*
                </label>
                <input type="number" min="1" value={tonnageRequested || ''} onChange={e => setTonnageRequested(parseInt(e.target.value) || undefined)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" placeholder="Enter tonnage" required={requestType === 'tonnage' || requestType === 'both'} data-id="element-3581" />
              </div>}
            <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3582">
              <div data-id="element-3583">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3584">
                  Date Needed*
                </label>
                <input type="date" value={requestedDate} onChange={e => setRequestedDate(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" required data-id="element-3585" />
              </div>
              <div data-id="element-3586">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3587">
                  Time Needed*
                </label>
                <input type="time" value={requestTime} onChange={e => setRequestTime(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" required data-id="element-3588" />
              </div>
            </div>
            <div className="mb-4" data-id="element-3589">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3590">
                Urgency
              </label>
              <select value={urgency} onChange={e => setUrgency(e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm p-2 border" data-id="element-3591">
                <option value="low" data-id="element-3592">Low - Plan ahead</option>
                <option value="medium" data-id="element-3593">Medium - Standard request</option>
                <option value="high" data-id="element-3594">High - Urgent need</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-3595">
              <div className="flex justify-between items-center mb-1" data-id="element-3596">
                <label className="block text-sm font-medium text-gray-700" data-id="element-3597">
                  Reason / Additional Details
                </label>
                <button type="button" onClick={toggleRecording} className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${isRecording ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3598">
                  {isRecording ? <StopCircleIcon size={12} data-id="element-3599" /> : <MicIcon size={12} data-id="element-3600" />}
                  {isRecording ? 'Stop' : 'Voice Input'}
                </button>
              </div>
              {recordingError && <div className="text-red-500 text-xs mb-2" data-id="element-3601">
                  {recordingError}
                </div>}
              <div className="relative" data-id="element-3602">
                <textarea ref={reasonInputRef} value={reason} onChange={e => setReason(e.target.value)} className={`w-full border-gray-300 rounded-md shadow-sm p-2 border ${isRecording ? 'border-red-400 bg-red-50' : ''}`} rows={3} placeholder="Explain why you need these materials..." data-id="element-3603"></textarea>
                {isRecording && <div className="absolute bottom-2 right-2 flex items-center gap-2 text-red-500 text-xs animate-pulse" data-id="element-3604">
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full" data-id="element-3605"></span>
                    Recording...
                  </div>}
              </div>
            </div>
            {urgency === 'high' && <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2" data-id="element-3606">
                <AlertTriangleIcon size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-3607" />
                <div className="text-sm text-yellow-700" data-id="element-3608">
                  High urgency requests will be prioritized but may incur
                  additional fees. Use this option only when truly necessary.
                </div>
              </div>}
          </div>
          <div className="p-4 border-t bg-gray-50 flex justify-between" data-id="element-3609">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-3610">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-3611">
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>;
};